import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Search, Download, Filter, RefreshCw, Calendar, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VisitClassification() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [uploadDates, setUploadDates] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState('all');
  const [filters, setFilters] = useState({
    week_ending: 'all',
    projection_type: 'all',
    agency: 'all',
    classification: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch available upload dates on mount
  useEffect(() => {
    fetchUploadDates();
  }, []);

  // Fetch visits when upload selection changes
  useEffect(() => {
    fetchData();
  }, [selectedUpload]);

  const fetchUploadDates = async () => {
    try {
      const response = await axios.get(`${API}/admin/upload-dates`);
      setUploadDates(response.data.upload_dates || []);
      // Auto-select the most recent upload if available
      if (response.data.upload_dates?.length > 0) {
        setSelectedUpload(response.data.upload_dates[0].upload_date);
      }
    } catch (error) {
      console.error('Error fetching upload dates:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedUpload && selectedUpload !== 'all') {
        params.append('upload_date', selectedUpload);
      }
      if (filters.week_ending && filters.week_ending !== 'all') params.append('week_ending', filters.week_ending);
      if (filters.projection_type && filters.projection_type !== 'all') params.append('projection_type', filters.projection_type);
      if (filters.agency && filters.agency !== 'all') params.append('agency', filters.agency);
      if (filters.classification && filters.classification !== 'all') params.append('classification', filters.classification);
      
      const response = await axios.get(`${API}/admin/visit-classification?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching visit classification:', error);
      toast.error('Failed to load visit data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchData();
  };

  const clearFilters = () => {
    setFilters({
      week_ending: 'all',
      projection_type: 'all',
      agency: 'all',
      classification: 'all'
    });
    setSearchTerm('');
  };

  const formatUploadDate = (uploadDate) => {
    try {
      const date = new Date(uploadDate.replace(' ', 'T') + ':00');
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return uploadDate;
    }
  };

  const exportToCSV = () => {
    if (!data?.visits) return;
    
    const headers = ['Therapist', 'Agency', 'Task Name', 'Status', 'Date', 'Weight', 'Classification', 'Is Eval', 'Is Discharge', 'Is MV', 'Week Ending', 'Projection Type'];
    const rows = data.visits.map(v => [
      v.therapist,
      v.agency,
      v.task_name,
      v.status,
      v.date,
      v.weight,
      v.classification,
      v.is_eval ? 'Y' : 'N',
      v.is_discharge ? 'Y' : 'N',
      v.is_mv ? 'Y' : 'N',
      v.week_ending,
      v.projection_type
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visit-classification-${selectedUpload || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Filter visits by search term
  const filteredVisits = data?.visits?.filter(v => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      v.therapist?.toLowerCase().includes(term) ||
      v.agency?.toLowerCase().includes(term) ||
      v.task_name?.toLowerCase().includes(term) ||
      v.classification?.toLowerCase().includes(term)
    );
  }) || [];

  const getClassificationBadge = (visit) => {
    const parts = [];
    
    if (visit.is_mv) {
      return <Badge variant="destructive" className="font-mono">0 MV</Badge>;
    }
    
    if (visit.weight === 0 && !visit.is_mv) {
      return <Badge variant="outline" className="bg-gray-100 text-gray-500 font-mono">0</Badge>;
    }
    
    parts.push(visit.weight.toString());
    if (visit.is_eval) parts.push('E');
    if (visit.is_discharge) parts.push('D');
    
    const className = visit.is_eval 
      ? 'bg-purple-100 text-purple-700 border-purple-300' 
      : visit.is_discharge 
        ? 'bg-orange-100 text-orange-700 border-orange-300'
        : 'bg-blue-100 text-blue-700 border-blue-300';
    
    return <Badge variant="outline" className={`font-mono font-bold ${className}`}>{parts.join(' ')}</Badge>;
  };

  // Get selected upload info for display
  const selectedUploadInfo = uploadDates.find(u => u.upload_date === selectedUpload);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Visit Classification Debug</h1>
          <p className="text-muted-foreground">
            View how each visit is weighted and classified per upload (E=Eval, D=Discharge, MV=Missed)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV} disabled={!data?.visits?.length}>
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Upload Date Selector - PRIMARY FILTER */}
      <Card className="p-4 border-primary/30 bg-primary/5">
        <div className="flex items-center gap-2 mb-3">
          <Upload size={18} className="text-primary" />
          <h3 className="font-semibold text-primary">Select Upload</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Select value={selectedUpload} onValueChange={setSelectedUpload}>
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Select an upload to view..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Uploads (Combined View)</SelectItem>
                {uploadDates.map((upload, idx) => (
                  <SelectItem key={idx} value={upload.upload_date}>
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-muted-foreground" />
                      <span>{formatUploadDate(upload.upload_date)}</span>
                      <span className="text-muted-foreground">
                        ({upload.count} records, {upload.projection_types?.join(', ')})
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedUploadInfo && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span><strong>Records:</strong> {selectedUploadInfo.count}</span>
              <span><strong>Type:</strong> {selectedUploadInfo.projection_types?.join(', ')}</span>
              <span><strong>Week:</strong> {selectedUploadInfo.weeks?.join(', ')}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Totals Summary */}
      {data?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Total Visits</p>
            <p className="text-3xl font-bold">{data.totals.total}</p>
          </Card>
          <Card className="p-4 text-center bg-primary/5">
            <p className="text-sm text-muted-foreground">Weighted Total</p>
            <p className="text-3xl font-bold text-primary">{data.totals.weighted_total}</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Weight = 2</p>
            <p className="text-3xl font-bold text-green-600">{data.totals.by_weight?.[2] || 0}</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Weight = 1</p>
            <p className="text-3xl font-bold text-blue-600">{data.totals.by_weight?.[1] || 0}</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Evals (E)</p>
            <p className="text-3xl font-bold text-purple-600">{data.totals.evals}</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">Discharges (D)</p>
            <p className="text-3xl font-bold text-orange-600">{data.totals.discharges}</p>
          </Card>
        </div>
      )}

      {/* Additional Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-muted-foreground" />
          <h3 className="font-medium">Additional Filters</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <Label className="text-xs">Week Ending</Label>
            <Select value={filters.week_ending} onValueChange={(v) => handleFilterChange('week_ending', v)}>
              <SelectTrigger>
                <SelectValue placeholder="All weeks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All weeks</SelectItem>
                {data?.available_filters?.weeks?.map(w => (
                  <SelectItem key={w} value={w}>{w}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Projection Type</Label>
            <Select value={filters.projection_type} onValueChange={(v) => handleFilterChange('projection_type', v)}>
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="Proj 1">Proj 1</SelectItem>
                <SelectItem value="Proj 2">Proj 2</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Agency</Label>
            <Select value={filters.agency} onValueChange={(v) => handleFilterChange('agency', v)}>
              <SelectTrigger>
                <SelectValue placeholder="All agencies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agencies</SelectItem>
                {data?.available_filters?.agencies?.slice(0, 50).map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Classification</Label>
            <Select value={filters.classification} onValueChange={(v) => handleFilterChange('classification', v)}>
              <SelectTrigger>
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="E">Evals only (E)</SelectItem>
                <SelectItem value="D">Discharges only (D)</SelectItem>
                <SelectItem value="MV">Missed Visits (MV)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Quick Search</Label>
            <div className="relative">
              <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input 
                className="pl-8" 
                placeholder="Search..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={applyFilters} className="flex-1">
              Apply
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-green-100 text-green-700 font-mono font-bold">2</Badge>
          <span className="text-muted-foreground">= OASIS (counts as 2)</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-blue-100 text-blue-700 font-mono">1</Badge>
          <span className="text-muted-foreground">= Regular visit</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-gray-100 text-gray-500 font-mono">0</Badge>
          <span className="text-muted-foreground">= Not counted</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-purple-100 text-purple-700 font-mono">E</Badge>
          <span className="text-muted-foreground">= Eval</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-orange-100 text-orange-700 font-mono">D</Badge>
          <span className="text-muted-foreground">= Discharge</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="destructive" className="font-mono">MV</Badge>
          <span className="text-muted-foreground">= Missed Visit</span>
        </div>
      </div>

      {/* Visits Table */}
      <Card className="overflow-hidden">
        <div className="max-h-[600px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3 font-medium">Classification</th>
                <th className="text-left p-3 font-medium">Therapist</th>
                <th className="text-left p-3 font-medium">Agency</th>
                <th className="text-left p-3 font-medium">Task Name</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Date</th>
                <th className="text-left p-3 font-medium">Week</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center p-8 text-muted-foreground">
                    Loading visits...
                  </td>
                </tr>
              ) : filteredVisits.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center p-8 text-muted-foreground">
                    {selectedUpload === 'all' ? 'No visits found. Select an upload above.' : 'No visits found for this upload.'}
                  </td>
                </tr>
              ) : (
                filteredVisits.map((visit, idx) => (
                  <tr key={idx} className="border-t hover:bg-muted/30">
                    <td className="p-3">
                      {getClassificationBadge(visit)}
                    </td>
                    <td className="p-3">{visit.therapist}</td>
                    <td className="p-3">{visit.agency}</td>
                    <td className="p-3 max-w-xs truncate" title={visit.task_name}>
                      {visit.task_name}
                    </td>
                    <td className="p-3">
                      {visit.status && (
                        <span className={visit.is_mv ? 'text-red-600 font-medium' : ''}>
                          {visit.status}
                        </span>
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap">{visit.date}</td>
                    <td className="p-3 whitespace-nowrap text-muted-foreground">{visit.week_ending}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredVisits.length > 0 && (
          <div className="p-3 border-t bg-muted/30 text-sm text-muted-foreground">
            Showing {filteredVisits.length} of {data?.totals?.total || 0} visits
          </div>
        )}
      </Card>
    </div>
  );
}
